import { OverlayPage } from '@components/partials/OverlayPage';

import React, { useState, useEffect } from 'react';

import { useNavSourceRedirect } from '@hooks/utils/dashboard';
import enhancedSecurity from '@assets/images/enhanced-security-large.png';
import { Button,  CheckCircleIcon, PlusIcon, Modal } from '@cloudclevr/components';
import { useIsMobile } from '@hooks/utils';
import { useNavigate } from 'react-router-dom';
import {
  AuthenticationResult,
  PublicClientApplication,
  Configuration,
} from '@azure/msal-browser';
import { scopeConnectionRequest, getHelixMsalConfig, msalConfig } from '@api/msalConfig';
import {
  useCheckMsAuditingEnabled,
  useHelixOnboarding,
  useConnectedServicesQuery,
} from '@hooks/connectedServices';
import { ServiceIntergrationResponse } from '@hooks/connectedServices/types';
import useUIStore from '@store/uiStore';
import { Variant } from '@components/ui/Toast';

const EnhancedSecurityPage: React.FC = () => {
  const redirectPath = useNavSourceRedirect(
    '/settings/manage-connected-services'
  );

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isAuditingModalOpen, setIsAuditingModalOpen] = useState(false);
  const [msTenantId, setMsTenantId] = useState<string | null>(null);
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { addToast } = useUIStore();

  const checkMsAuditingEnabled = useCheckMsAuditingEnabled();
  const helixOnboarding = useHelixOnboarding();
  const { data: connectedServices } = useConnectedServicesQuery();

  // Check if Microsoft is connected (required for Enhanced Security page)
  const isMicrosoftConnected = connectedServices?.some((service: ServiceIntergrationResponse) => service.vendor === 0);
   

  const handleConnectEnhancedInsights = async () => {
    // Check if Microsoft is connected first
    if (!isMicrosoftConnected) {
      addToast({
        variant: Variant.Error,
        message: `Microsoft 365 must be connected first to enable Enhanced Assurance Insights.`,
        closeable: true,
      });
      return;
    }

    // Get tenant ID using Microsoft login popup (same way as ConnectServiceModal)
    // Find the Microsoft service (vendor === 0)
    const msService = connectedServices?.find(
      (service: ServiceIntergrationResponse) => service.vendor === 0
    );

    // Get the tenantId from that service
    const tenantId = msService?.tenantId;    
    if (!tenantId) {
      addToast({
        variant: Variant.Error,
        message: `Unable to get Microsoft tenant information.`,
        closeable: true,
      });
      return;
    }
    
    try {
      // Check if Microsoft auditing is enabled
      const isAuditingEnabled = await checkMsAuditingEnabled.mutateAsync({
        tenantId: tenantId
      });
      
      if (isAuditingEnabled) {
        // If auditing is enabled, show Helix consent dialog
        const msalInstance = new PublicClientApplication(getHelixMsalConfig());
        await msalInstance.initialize();
        
        try {
          // Show Helix consent popup
          const response = await msalInstance.loginPopup({ 
            ...scopeConnectionRequest, 
            prompt: 'consent' 
          });
          
          if (response) {            
            // After Helix consent is granted, onboard to Helix
            helixOnboarding.mutate(
              { tenantId: tenantId },
              {
                onSuccess: (response) => {
                  if (response.isSuccess) {
                    addToast({
                      variant: Variant.Success,
                      message: `Enhanced Assurance Insights has been successfully connected.`,
                      closeable: true,
                    });
                    // Navigate to connected services after success
                    navigate('/settings/manage-connected-services');
                  } else {
                    addToast({
                      variant: Variant.Warning,
                      message: response.errorMessage || `Enhanced Assurance Insights connection had an issue, but you can try again later.`,
                      closeable: true,
                    });
                  }
                },
                onError: (error) => {
                  addToast({
                    variant: Variant.Error,
                    message: `Something went wrong connecting Enhanced Assurance Insights.`,
                    closeable: true,
                  });
                }
              }
            );
          }
        } catch (helixConsentError) {
          // User cancelled Helix consent or consent failed
          addToast({
            variant: Variant.Warning,
            message: `Consent is required to connect Enhanced Assurance Insights. Please try again.`,
            closeable: true,
          });
        }
      } else {
        // If auditing is not enabled, show auditing modal
        setIsAuditingModalOpen(true);
        setMsTenantId(tenantId); // Store tenantId for use in auditing modal
      }
    } catch (error) {
      addToast({
        variant: Variant.Error,
        message: `Something went wrong checking auditing status.`,
        closeable: true,
      });
    }
  };

  const handleOpenMicrosoftAuditing = () => {
    window.open('https://security.microsoft.com/auditlogsearch', '_blank');
  };

  const handleAuditingComplete = () => {
    // Close auditing modal
    setIsAuditingModalOpen(false);
    
    // Check if auditing is now enabled
    if (msTenantId) {
      checkMsAuditingEnabled.mutate(
        { tenantId: msTenantId },
        {
          onSuccess: (isEnabled) => {
            if (isEnabled) {
              // If auditing is now enabled, onboard to Helix
              helixOnboarding.mutate(
                { tenantId: msTenantId },
                {
                  onSuccess: (response) => {
                    if (response.isSuccess) {
                      addToast({
                        variant: Variant.Success,
                        message: `Enhanced Assurance Insights has been successfully connected.`,
                        closeable: true,
                      });
                      // Navigate to connected services after success
                      navigate('/settings/manage-connected-services');
                    } else {
                      addToast({
                        variant: Variant.Warning,
                        message: response.errorMessage || `Enhanced Assurance Insights connection had an issue, but you can try again later.`,
                        closeable: true,
                      });
                    }
                  },
                  onError: (error) => {
                    addToast({
                      variant: Variant.Error,
                      message: `Something went wrong connecting Enhanced Assurance Insights.`,
                      closeable: true,
                    });
                  }
                }
              );
            } else {
              // If still not enabled, show message
              addToast({
                variant: Variant.Warning,
                message: `Microsoft 365 Auditing is still not enabled. You can try again later.`,
                closeable: true,
              });
            }
          },
          onError: () => {
            addToast({
              variant: Variant.Error,
              message: `Something went wrong checking auditing status.`,
              closeable: true,
            });
          }
        }
      );
    }
  };

  const handleSkipAuditing = () => {
    setIsAuditingModalOpen(false);
    
    // Show notification for skipped Enhanced Insights setup
    addToast({
      variant: Variant.Info,
      message: `You can enable Enhanced Assurance Insights later.`,
      closeable: true,
    });
  };

  const permissions = [
    { name: 'Application.Read.All', description: 'Read all applications' },
    { name: 'AuditLog.Read.All', description: 'Read all audit log data' },
    { name: 'Device.Read.All', description: 'Read all devices' },
    { name: 'Directory.Read.All', description: 'Read directory data' },
    {
      name: 'IdentityRiskEvent.Read.All',
      description: 'Read all identity risk event information',
    },
    {
      name: 'IdentityRiskyUser.Read.All',
      description: 'Read all identity risky user information',
    },
    {
      name: 'MailboxSettings.Read',
      description: 'Read all user mailbox settings',
    },
    {
      name: 'Policy.Read.All',
      description: "Read your organization's policies",
    },
    { name: 'Reports.Read.All', description: 'Read all usage reports' },
    { name: 'SecurityAlert.Read.All', description: 'Read all security alerts' },
    {
      name: 'SecurityEvents.Read.All',
      description: 'Read all security events',
    },
    { name: 'User.Read.All', description: 'Read all users' },
    { name: 'User.Read (Delegated permission)', description: 'Sign in and read user profile' },
    { name: 'UserAuthenticationMethod.Read.All', description: 'Read all users\' authentication methods' },
  ];

  return (
    <OverlayPage
      isFooterVisible={false}
      contentClassNames={''}
      path={redirectPath}
      headerTitle={``}
    >
      <div className="bg-white py-0 lg:bg-transparent lg:py-2">
        <div className="container mx-auto px-0 md:px-6 xl:px-16">
          <div className="relative h-auto rounded-[4px] bg-interfaceColor-90 shadow-inner xl:h-[505px]">
            <div className="flex h-full flex-col lg:flex-row">
              <div className="flex h-full flex-col justify-between gap-y-4 p-8 lg:w-1/2">
                <h2 className="max-w-[500px] text-32 leading-[32px] text-white lg:text-[60px] lg:leading-[60px]">
                  In-depth
                  <br /> assurance
                  <br /> insights at your
                  <br /> fingertips
                </h2>
                <p className="max-w-[500px] text-16 text-white">
                  Enhanced Assurance Insights give you more knowledge than ever
                  before about how safe your Microsoft estate is, and how to
                  make it safer for your organisation.
                </p>
                <Button
                  testId='get-enhanced-assurance-insights-button'
                  variant="primary"
                  className="flex w-full items-center lg:w-fit"
                  size="medium"
                  loading={checkMsAuditingEnabled.isPending || helixOnboarding.isPending}
                  onClick={handleConnectEnhancedInsights}
                >
                  <PlusIcon
                    color="text-interfaceColor-100"
                    classNames="mr-2 mt-[2px]"
                    size="3"
                  />
                  <span className="text-14 text-interfaceColor-100 lg:text-16">
                    Get Enhanced Assurance Insights
                  </span>
                </Button>
              </div>
              <div className="lg:w-1/2">
                <img
                  src={enhancedSecurity}
                  alt="Assurance Insights"
                  className="relative top-auto h-auto w-full object-cover lg:-top-6 lg:w-[548px] xl:absolute xl:right-4 xl:h-[524px]"
                />
              </div>
            </div>
          </div>
          <div className="relative z-10 mx-auto -mt-44 block max-w-2xl bg-white px-4 py-4 shadow-sm lg:mt-12 lg:bg-transparent lg:px-0 lg:py-0 lg:shadow-none ">
            <h3 className="mb-1 text-24 leading-[26px] text-interfaceColor-100 lg:text-28 lg:leading-[38px]">
              Everything you need to know to stay safe
            </h3>
            <p className="mb-2 text-16 text-interfaceColor-100">
              Discover how this add-on can revolutionise your security strategy
              with a fresh wave of assurance insights tailored specifically for
              Clevr360 users:
            </p>
            <ul className="list-inside space-y-4">
              <li>
                <CheckCircleIcon
                  color={'text-interfaceColor-100'}
                  classNames="h-4 w-4 mr-2 align-middle inline -mt-[2px]"
                />
                <strong>Uncover suspicious user account activity:</strong> Gain
                deep insights into user behaviour, including suspicious travel
                patterns, intrusive app usage, MFA failures, and admin login
                activity.
              </li>
              <li>
                <CheckCircleIcon
                  color={'text-interfaceColor-100'}
                  classNames="h-4 w-4 mr-2 align-middle inline -mt-[2px]"
                />
                <strong>Manage external apps accessing Microsoft data:</strong>{' '}
                Track and control permissions granted to external apps during
                user registration ensuring your data remains secure.
              </li>
              <li>
                <CheckCircleIcon
                  color={'text-interfaceColor-100'}
                  classNames="h-4 w-4 mr-2 align-middle inline -mt-[2px]"
                />
                <strong>Monitor and mitigate failed Microsoft logins:</strong>{' '}
                Identify and address failed login attempts swiftly by
                pinpointing the user account, login location, and frequency of
                failed logins.
              </li>
              <li>
                <CheckCircleIcon
                  color={'text-interfaceColor-100'}
                  classNames="h-4 w-4 mr-2 align-middle inline -mt-[2px]"
                />
                <strong>Stay ahead of the curve with software updates:</strong>{' '}
                Keep your systems secure by identifying and addressing outdated
                operating systems and internet browsers promptly.
              </li>
              <li>
                <CheckCircleIcon
                  color={'text-interfaceColor-100'}
                  classNames="h-4 w-4 mr-2 align-middle inline -mt-[2px]"
                />
                <strong>Enhance mailbox security:</strong> Detect and prevent
                potential threats by identifying mailboxes with auto-forwarding
                enabled and monitoring delegated access across all mailboxes.
              </li>
            </ul>
            <p className="mb-6 mt-3 text-gray-700">
              Harness the power of Clevr360 to safeguard your
              organisation&apos;s digital assets and ensure peace of mind.
            </p>
            <h3 className="mb-1 text-28 text-interfaceColor-100">
              Get Enhanced Assurance Insights
            </h3>
            <p className="mb-6 text-gray-700">
              The activation process takes a couple of days as it requires a
              manual step by a Clevr360 engineer. Once configuration is
              completed you will be notified and sent a link for you to accept a
              set of read-only permissions within Microsoft365.{' '}
              <span
                onClick={() => setIsModalOpen(true)}
                className="cursor-pointer border-b border-b-interfaceColor-40"
              >
                See required permissions here
              </span>
              .
            </p>
            <Button
              testId='get-enhanced-assurance-insights-navigate-button'
              variant="primary"
              className="flex w-full items-center"
              size="medium"
              loading={checkMsAuditingEnabled.isPending || helixOnboarding.isPending}
              onClick={handleConnectEnhancedInsights}
            >
              <PlusIcon
                color="text-interfaceColor-100"
                classNames="mr-2 mt-[2px]"
                size="3"
              />
              <span className="text-interfaceColor-100">
                Get Enhanced Assurance Insights
              </span>
            </Button>
          </div>
        </div>

        {/* Permissions Modal */}
        <Modal
          testId='enhanced-assurance-insights-permissions-modal'
          headerTxt={'Enhanced Assurance Insights permissions'}
          isTruncatedHeader={false}
          isOpen={isModalOpen}
          modalClassName={'mt-0'}
          size={'medium'}
          position={isMobile ? 'bottom' : 'default'}
          showCloseBtn={true}
          onClose={() => {
            setIsModalOpen(false);
          }}
          actionButtons={
            <div className="flex flex-col gap-2 md:flex-row"></div>
          }
          contentClassName="flex flex-column !items-start !justify-start bg-white"
        >
          <div className="w-full">
            <p className="mb-4">
              Once configuration is completed, you will be asked to accept these
              read-only application permissions.
            </p>
            <div className="max-h-[400px] overflow-y-scroll rounded-lg border border-interfaceColor-40 p-2 md:max-h-[300px]">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-2">
                {permissions.map((permission, index) => (
                  <div key={index}>
                    <h4 className="text-14 font-semibold text-interfaceColor-100">
                      {permission.name}
                    </h4>
                    <p className="text-14 text-interfaceColor-100">
                      {permission.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Modal>

        {/* Microsoft Auditing Modal */}
        <Modal
          testId='microsoft-auditing-modal'
          headerTxt="Enable Auditing in Microsoft 365 to connect Enhanced Assurance Insights"
          modalClassName={'mt-0'}
          isTruncatedHeader={false}
          isOpen={isAuditingModalOpen}
          size={isMobile ? 'small' : 'large'}
          position={isMobile ? 'bottom' : 'default'}
          showCloseBtn={true}
          onClose={() => {
            setIsAuditingModalOpen(false);
          }}
          actionButtons={
            <div className="mt-5 flex flex-col gap-2 lg:flex-row">            
              <Button
                testId='ive-done-this-button'
                variant="primary"
                loading={checkMsAuditingEnabled.isPending || helixOnboarding.isPending}
                onClick={handleAuditingComplete}
              >
                I&apos;ve done this
              </Button>
              <Button
                testId='i-dont-want-these-insights-button'
                variant="outlineLight"
                onClick={handleSkipAuditing}
              >
                I don&apos;t want these insights
              </Button>
            </div>
          }
          contentClassName="flex !flex-row !items-start !justify-start bg-white"
        >
          <div className="w-full">
            <p className="mb-4">
              Enabling Auditing within Microsoft 365 brings several advantages to you.
            </p>
            <ul className="mb-4 pl-8 text-black text-[16px]">
              <li className="mb-2 flex items-start gap-2">
                <span className="mt-1 flex h-5 w-5 items-center justify-center rounded-full bg-white">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <circle cx="8" cy="8" r="7" stroke="#000" strokeWidth="1" fill="none"/>
                    <path d="M5 8.5l2 2 4-4" stroke="#000" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </span>
                Clevr360 can provide the Enhanced Security Insights
              </li>
              <li className="mb-2 flex items-start gap-2">
                <span className="mt-1 flex h-5 w-5 items-center justify-center rounded-full bg-white">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <circle cx="8" cy="8" r="7" stroke="#000" strokeWidth="1" fill="none"/>
                    <path d="M5 8.5l2 2 4-4" stroke="#000" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </span>
                Audit logs within Microsoft 365 can be used to investigate compromised accounts and determine the scope of data compromise, making investigations simpler and more effective.
              </li>
              <li className="mb-2 flex items-start gap-2">
                <span className="mt-1 flex h-5 w-5 items-center justify-center rounded-full bg-white">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <circle cx="8" cy="8" r="7" stroke="#000" strokeWidth="1" fill="none"/>
                    <path d="M5 8.5l2 2 4-4" stroke="#000" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </span>
                Auditing ensures compliance with regulatory requirements, industry standards, and internal policies.
              </li>
            </ul>
            <p>
                To check and turn on auditing, go to{' '}
                <a
                  href="#"
                  className="text-blue-500 underline"
                  onClick={(e) => {
                    e.preventDefault();
                    handleOpenMicrosoftAuditing();
                  }}
                >
                  https://security.microsoft.com/auditlogsearch
                </a>
                . If auditing is turned off, you will see a blue button &quot;Start recording user and admin activity&quot;. Click to turn on auditing.
              </p>
          </div>
        </Modal>
      </div>
    </OverlayPage>
  );
};

export default EnhancedSecurityPage;